import { AssessmentAnswer } from '../../generated/graphql';
import {
  isAutofillAssessmentAnswer,
  isDatePickerAssessmentAnswer,
  isFreeTextAssessmentAnswer,
  isRichTextAssessmentAnswer,
  isMultipleChoiceAssessmentAnswer,
  isMultipleChoiceScoredAssessmentAnswer,
  isMultiSelectAssessmentAnswer,
  isSignatureAssessmentAnswer,
  isInitialsAssessmentAnswer,
} from './assessments';
import {
  AutofillAnswerWithAlias,
  DatePickerAnswerWithAlias,
} from './answerTypes';

/**
 * Type-safe interface for answers with question field
 */
interface AnswerWithQuestion {
  question?: string;
}

/**
 * Extracts the question text from an AssessmentAnswer.
 * Works for all answer types that have a question field.
 *
 * @param answer - The assessment answer
 * @returns The question text, or null if not available
 */
export const getAnswerQuestion = (answer: AssessmentAnswer): string | null => {
  // All answer types have a question field in the base type
  // For Autofill and DatePicker, cast to intersection type to ensure type safety
  if (isAutofillAssessmentAnswer(answer)) {
    const autofillAnswer = answer as AutofillAnswerWithAlias;
    return autofillAnswer.question ?? null;
  }
  if (isDatePickerAssessmentAnswer(answer)) {
    const datePickerAnswer = answer as DatePickerAnswerWithAlias;
    return datePickerAnswer.question ?? null;
  }

  // For other answer types, use generic interface
  const answerWithQuestion = answer as AnswerWithQuestion;
  return answerWithQuestion?.question ?? null;
};

/**
 * Extracts the answer value from an AssessmentAnswer.
 * Handles all answer types including Autofill and DatePicker with aliased fields.
 *
 * @param answer - The assessment answer
 * @returns The answer value as a string, or null if not available
 */
export const getAnswerValue = (answer: AssessmentAnswer): string | null => {
  // Autofill answers (using aliased field from GraphQL query)
  if (isAutofillAssessmentAnswer(answer)) {
    // Cast to intersection type to access aliased field
    const autofillAnswer = answer as AutofillAnswerWithAlias;
    return autofillAnswer.autofillAnswer ?? null;
  }

  // DatePicker answers (using aliased field from GraphQL query)
  if (isDatePickerAssessmentAnswer(answer)) {
    // Cast to intersection type to access aliased field
    const datePickerAnswer = answer as DatePickerAnswerWithAlias;
    const dateValue = datePickerAnswer.datePickerAnswer;
    // DatePicker answer might be a Date object or string
    if (dateValue instanceof Date) {
      return dateValue.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    }
    return dateValue ?? null;
  }

  // Text answers (free text and rich text)
  if (
    isFreeTextAssessmentAnswer(answer) ||
    isRichTextAssessmentAnswer(answer)
  ) {
    return answer.answer ?? null;
  }

  // Multiple choice answers (both scored and regular)
  if (
    isMultipleChoiceAssessmentAnswer(answer) ||
    isMultipleChoiceScoredAssessmentAnswer(answer)
  ) {
    return answer.answer ?? null;
  }

  // Signature and initials answers
  if (
    isSignatureAssessmentAnswer(answer) ||
    isInitialsAssessmentAnswer(answer)
  ) {
    return answer.signatureName ?? null;
  }

  // Multi-select answers - return first value or empty string
  if (isMultiSelectAssessmentAnswer(answer)) {
    return answer.answers?.[0] ?? null;
  }

  return null;
};

/**
 * Extracts the answer value as a string array (for multi-select answers).
 *
 * @param answer - The assessment answer
 * @returns Array of answer values, or empty array if not applicable
 */
export const getAnswerValues = (answer: AssessmentAnswer): string[] => {
  if (isMultiSelectAssessmentAnswer(answer)) {
    return answer.answers ?? [];
  }
  return [];
};
